import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Input, InputGroup, InputGroupText, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { api } from '../../utils';

const DEFAULT_SIZE_PER_PAGE = 100;
const SIZE_PER_PAGE_LIST = [
  { text: '50', value: 50 },
  { text: '100', value: 100 },
  { text: '250', value: 250 },
];

const getPaginationOptions = () => ({
  sizePerPage: DEFAULT_SIZE_PER_PAGE,
  sizePerPageList: SIZE_PER_PAGE_LIST,
  hideSizePerPage: false,
  showTotal: true,
});

const ProjectDetail = () => {
  const { id } = useParams();
  const [projectDomains, setProjectDomains] = useState([]);
  const [databaseDomains, setDatabaseDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDatabaseDomains, setLoadingDatabaseDomains] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addingDatabaseDomains, setAddingDatabaseDomains] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [databaseMessage, setDatabaseMessage] = useState(null);
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [selectedDatabaseDomains, setSelectedDatabaseDomains] = useState([]);
  const [domainSearch, setDomainSearch] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProjectDetail();
  }, [id]);

  const fetchProjectDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`link-building/project-domains/?project=${id}`);
      setProjectDomains(response.data);
    } catch (error) {
      console.error('Error fetching project domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDatabaseDomains = async (search = domainSearch) => {
    setLoadingDatabaseDomains(true);
    setDatabaseMessage(null);

    try {
      const params = {};
      if (search) params.search = search;
      const response = await api.get('link-building/domains/', { params });
      setDatabaseDomains(response.data);
      setSelectedDatabaseDomains([]);
    } catch (error) {
      console.error('Error fetching database domains:', error);
      setDatabaseMessage('Errore durante il caricamento dei domini dal DB.');
    } finally {
      setLoadingDatabaseDomains(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadMessage(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`link-building/projects/${id}/upload_excel/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setUploadMessage(`Success! Created: ${response.data.links_created}, Updated: ${response.data.links_updated}`);
      fetchProjectDetail();
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadMessage(error.response?.data?.error || 'Error uploading file.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const boolFormatter = (cell) => {
    if (cell === true) return <span className="text-success"><i className="fas fa-check" /></span>;
    if (cell === false) return <span className="text-danger"><i className="fas fa-times" /></span>;
    return '-';
  };

  const numberFormatter = (cell) => {
    if (cell == null) return '-';
    return Number(cell).toLocaleString('it-IT');
  };

  const handleDeleteLink = async (linkId, domainName) => {
    if (window.confirm(`Sei sicuro di voler rimuovere il link per "${domainName}" da questo progetto?`)) {
      try {
        await api.delete(`link-building/project-domains/${linkId}/`);
        setSelectedLinks((prev) => prev.filter((selectedId) => selectedId !== linkId));
        fetchProjectDetail();
      } catch (error) {
        console.error('Error deleting link:', error);
        alert(error.response?.data?.error || 'Errore durante la rimozione del link.');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLinks.length === 0) return;

    if (
      window.confirm(
        `Sei sicuro di voler rimuovere i ${selectedLinks.length} link selezionati da questo progetto?`
      )
    ) {
      try {
        await api.post('link-building/project-domains/bulk_delete/', { ids: selectedLinks });
        setSelectedLinks([]);
        fetchProjectDetail();
      } catch (error) {
        console.error('Error deleting links in bulk:', error);
        alert(error.response?.data?.error || "Errore durante l'eliminazione multipla.");
      }
    }
  };

  const handleAddDatabaseDomains = async () => {
    if (selectedDatabaseDomains.length === 0) return;

    setAddingDatabaseDomains(true);
    setDatabaseMessage(null);

    try {
      const response = await api.post(`link-building/projects/${id}/add_domains/`, {
        domain_ids: selectedDatabaseDomains,
      });
      setDatabaseMessage(response.data.status);
      setSelectedDatabaseDomains([]);
      fetchProjectDetail();
    } catch (error) {
      console.error('Error adding database domains:', error);
      setDatabaseMessage(error.response?.data?.error || "Errore durante l'aggiunta dei domini.");
    } finally {
      setAddingDatabaseDomains(false);
    }
  };

  const actionFormatter = (cell, row) => (
    <Button
      color="danger"
      size="sm"
      outline
      onClick={() => handleDeleteLink(row.id, row.domain?.name)}
      title="Rimuovi dal progetto"
    >
      <i className="fas fa-trash" />
    </Button>
  );

  const getSortedSuppliers = (domain) => {
    if (!domain?.suppliers || domain.suppliers.length === 0) return [];
    return [...domain.suppliers].sort((a, b) => {
      const priceA = parseFloat(a.price);
      const priceB = parseFloat(b.price);
      if (isNaN(priceA) && isNaN(priceB)) return 0;
      if (isNaN(priceA)) return 1;
      if (isNaN(priceB)) return -1;
      return priceA - priceB;
    });
  };

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '-';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2,
    }).format(value);
  };

  const vendorFormatter = (cell, row) => {
    const suppliers = getSortedSuppliers(row);
    if (suppliers.length === 0) return '-';
    return suppliers.map((supplierRow) => supplierRow.supplier?.name).filter(Boolean).join(', ');
  };

  const priceFormatter = (cell, row) => {
    const suppliers = getSortedSuppliers(row);
    if (suppliers.length === 0) return '-';

    const validPrices = suppliers.map((supplierRow) => parseFloat(supplierRow.price)).filter((price) => !isNaN(price));
    const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : null;

    return (
      <div className="link-building-price-list">
        {suppliers.map((supplierRow) => {
          const price = parseFloat(supplierRow.price);
          const isLowest = minPrice != null && price === minPrice;
          return (
            <div key={supplierRow.id} className={isLowest ? 'font-weight-bold text-success' : ''}>
              {formatCurrency(price)}
              <span className="text-muted ml-1">({supplierRow.supplier?.name || '-'})</span>
            </div>
          );
        })}
      </div>
    );
  };

  const columns = [
    { dataField: 'domain.name', text: 'Domain Target', sort: true },
    { dataField: 'domain.categoria', text: 'Categoria', sort: true },
    { dataField: 'url_articolo', text: 'URL Articolo', sort: true },
    { dataField: 'link_interno', text: 'Target (Interno)', sort: true },
    { dataField: 'fornitore_name', text: 'Fornitore', sort: true },
    { dataField: 'domain.ahrefs_dr', text: 'DR', sort: true },
    { dataField: 'domain.ahrefs_org_traffic', text: 'Traffic (Ahrefs)', sort: true, formatter: numberFormatter },
    { dataField: 'domain.ahrefs_refdomains', text: 'Ref. Domains', sort: true, formatter: numberFormatter },
    { dataField: 'status_code', text: 'HTTP Status', sort: true },
    { dataField: 'pagina_indicizzabile', text: 'Indexable', sort: true, formatter: boolFormatter },
    { dataField: 'link_presente', text: 'Link Present', sort: true, formatter: boolFormatter },
    { dataField: 'assenza_attributi', text: 'Dofollow', sort: true, formatter: boolFormatter, title: () => 'Assenza Attributi' },
    { dataField: 'data_di_check', text: 'Last Check', sort: true },
    {
      dataField: 'actions',
      text: 'Azioni',
      isDummyField: true,
      formatter: actionFormatter,
      headerStyle: { width: '80px', textAlign: 'center' },
      style: { textAlign: 'center' },
    },
  ];

  const selectRow = {
    mode: 'checkbox',
    clickToSelect: false,
    selected: selectedLinks,
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedLinks((prev) => [...prev, row.id]);
      } else {
        setSelectedLinks((prev) => prev.filter((value) => value !== row.id));
      }
    },
    onSelectAll: (isSelect, rows) => {
      const ids = rows.map((row) => row.id);
      if (isSelect) {
        setSelectedLinks((prev) => [...new Set([...prev, ...ids])]);
      } else {
        setSelectedLinks((prev) => prev.filter((value) => !ids.includes(value)));
      }
    },
  };

  const databaseColumns = [
    { dataField: 'name', text: 'Domain', sort: true },
    { dataField: 'categoria', text: 'Categoria', sort: true },
    { dataField: 'topic', text: 'Topic', sort: true },
    { dataField: 'regione', text: 'Regione', sort: true },
    { dataField: 'vendor', isDummyField: true, text: 'Vendor', formatter: vendorFormatter },
    { dataField: 'price', isDummyField: true, text: 'Prezzo', formatter: priceFormatter },
    { dataField: 'rating', text: 'Rating', sort: true },
    { dataField: 'ahrefs_dr', text: 'DR', sort: true },
    { dataField: 'ahrefs_org_traffic', text: 'Traffic (Ahrefs)', sort: true, formatter: numberFormatter },
  ];

  const projectDomainIds = new Set(projectDomains.map((projectDomain) => projectDomain.domain?.id).filter(Boolean));
  const availableDatabaseDomains = databaseDomains.filter((domain) => !projectDomainIds.has(domain.id));

  const databaseSelectRow = {
    mode: 'checkbox',
    clickToSelect: false,
    selected: selectedDatabaseDomains,
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedDatabaseDomains((prev) => [...prev, row.id]);
      } else {
        setSelectedDatabaseDomains((prev) => prev.filter((value) => value !== row.id));
      }
    },
    onSelectAll: (isSelect, rows) => {
      const ids = rows.map((row) => row.id);
      if (isSelect) {
        setSelectedDatabaseDomains((prev) => [...new Set([...prev, ...ids])]);
      } else {
        setSelectedDatabaseDomains((prev) => prev.filter((value) => !ids.includes(value)));
      }
    },
  };

  return (
    <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
      <div className="container-fluid">
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center flex-wrap">
                <h3 className="mb-0">Project Details (ID: {id})</h3>
                <div className="d-flex align-items-center flex-wrap mt-3 mt-md-0">
                  {selectedLinks.length > 0 && (
                    <Button
                      color="danger"
                      size="sm"
                      onClick={handleBulkDelete}
                      className="mr-3"
                      style={{ height: '31px', padding: '0 15px' }}
                    >
                      <i className="fas fa-trash mr-2" /> Elimina Selezionati ({selectedLinks.length})
                    </Button>
                  )}

                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                  />

                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                    style={{ height: '31px', padding: '0 15px' }}
                  >
                    <i className="fas fa-upload mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Link Excel'}
                  </Button>

                  {uploadMessage && <span className="ml-2 text-sm text-success">{uploadMessage}</span>}
                </div>
              </CardHeader>
              <CardBody>
                <div className="mb-4 pb-4 border-bottom">
                  <div className="d-flex align-items-center flex-wrap mb-3">
                    <h4 className="mb-2 mr-3">Domini dal DB</h4>
                    <InputGroup size="sm" className="input-group-alternative mr-2 mb-2" style={{ maxWidth: '320px' }}>
                      <InputGroupText>
                        <i className="fas fa-search" />
                      </InputGroupText>
                      <Input
                        type="text"
                        placeholder="Search domain..."
                        value={domainSearch}
                        onChange={(event) => setDomainSearch(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') fetchDatabaseDomains();
                        }}
                      />
                    </InputGroup>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => fetchDatabaseDomains()}
                      disabled={loadingDatabaseDomains}
                      className="mr-2 mb-2"
                      style={{ height: '31px', padding: '0 15px' }}
                    >
                      <i className="fas fa-database mr-2" />
                      {loadingDatabaseDomains ? 'Loading...' : 'Cerca DB'}
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      onClick={handleAddDatabaseDomains}
                      disabled={selectedDatabaseDomains.length === 0 || addingDatabaseDomains}
                      className="mb-2"
                      style={{ height: '31px', padding: '0 15px' }}
                    >
                      <i className="fas fa-plus mr-2" />
                      {addingDatabaseDomains ? 'Adding...' : `Aggiungi (${selectedDatabaseDomains.length})`}
                    </Button>
                    {databaseMessage && <span className="ml-2 text-sm text-success">{databaseMessage}</span>}
                  </div>
                  {databaseDomains.length > 0 && (
                    <div className="link-building-table">
                      <BootstrapTable
                        bootstrap4
                        keyField="id"
                        data={availableDatabaseDomains}
                        columns={databaseColumns}
                        selectRow={databaseSelectRow}
                        wrapperClasses="table-responsive link-building-table-scroll"
                        pagination={paginationFactory(getPaginationOptions())}
                        noDataIndication="Nessun dominio disponibile"
                      />
                    </div>
                  )}
                </div>
                <div
                  className="mb-4 text-muted text-sm px-3 py-2 border-left border-info bg-secondary"
                  style={{ borderWidth: '4px !important' }}
                >
                  <i className="fas fa-info-circle mr-2 text-info" />
                  <strong>Istruzioni Excel:</strong> Caricare un file <code>.xlsx</code> o <code>.xls</code>. <br />
                  <span className="ml-4">
                    <strong>Colonne richieste:</strong> <code>url articolo</code>, <code>link interno</code>, <code>fornitore</code>.
                  </span>
                </div>
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="link-building-table">
                    <BootstrapTable
                      bootstrap4
                      keyField="id"
                      data={projectDomains}
                      columns={columns}
                      selectRow={selectRow}
                      wrapperClasses="table-responsive link-building-table-scroll"
                      pagination={paginationFactory(getPaginationOptions())}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ProjectDetail;
